import {
  faDownload,
  faFile,
  faTrash,
} from "@fortawesome/pro-regular-svg-icons";

import { FileCard } from "src/base-components/FileCard";
import { EllipsisOptionsDropdown } from "src/base-components/OptionsDropdown/EllipsisOptionsDropdown";
import {
  MENU_DIVIDER,
  type OptionsDropdownElement,
} from "src/base-components/OptionsDropdown/OptionsDropdownItems";
import { CaseFileCard } from "src/cases/caseView/CaseFileCard";
import { Accordion } from "src/design-system/Accordion";
import { IconButton } from "src/design-system/Icon";
import { Pill } from "src/design-system/Pill";
import { Tooltip } from "src/design-system/Tooltip";
import { type Environment } from "src/hooks/useEnvironment";

export type FileCategoryBase = {
  category_key: string;
  display_name: string;
  description?: string;
};

export type FileCategoryData = FileCategoryBase & {
  id: string;
  file_ids: string[];
};

type FileCategoryProps = {
  category: FileCategoryData;
  env: Environment;
  fileCategories: FileCategoryData[];
  onDeleteFile: (fileId: string, fileName: string, categoryKey: string) => void;
  onMoveFile: (
    fileId: string,
    fileName: string,
    fromCategory: string,
    toCategory: string,
  ) => void;
  onDownloadCategory: (categoryKey: string) => void;
  canEdit: boolean;
  canDelete: boolean;
  isDownloadDisabled: boolean;
  newlyUploadedFileIds?: string[];
};

export const FileCategory: React.FC<FileCategoryProps> = ({
  category,
  env,
  fileCategories,
  onDeleteFile,
  onMoveFile,
  onDownloadCategory,
  canEdit,
  canDelete,
  isDownloadDisabled,
  newlyUploadedFileIds,
}) => {
  const fileCount = category.file_ids.length;
  const hasFiles = fileCount > 0;

  const otherCategories = fileCategories.filter(
    (cat) => cat.category_key !== category.category_key,
  );

  const buildFileDropdownElements = (
    fileId: string,
    fileName: string,
  ): OptionsDropdownElement[] => {
    const moveElements: OptionsDropdownElement[] =
      canEdit && otherCategories.length > 0
        ? [
            { key: "header", header: "Move to:" },
            ...otherCategories.map((cat) => ({
              key: cat.display_name,
              action: () =>
                onMoveFile(
                  fileId,
                  fileName,
                  category.category_key,
                  cat.category_key,
                ),
            })),
          ]
        : [];

    const deleteElements: OptionsDropdownElement[] = canDelete
      ? [
          {
            key: "Delete file",
            action: () => onDeleteFile(fileId, fileName, category.category_key),
            icon: faTrash,
          },
        ]
      : [];

    const divider =
      moveElements.length > 0 && deleteElements.length > 0
        ? [MENU_DIVIDER]
        : [];

    return [...moveElements, ...divider, ...deleteElements];
  };

  return (
    <Accordion.Item
      isDisabled={!hasFiles}
      rightSlot={
        <div className="flex items-center gap-1">
          {hasFiles && (
            <Tooltip title="Download all files in this category" asChild>
              <IconButton
                aria-label={`Download all files in ${category.display_name}`}
                dataLoc={`download-category-${category.category_key}`}
                disabled={isDownloadDisabled}
                icon={faDownload}
                size="xs"
                onClick={() => onDownloadCategory(category.category_key)}
              />
            </Tooltip>
          )}
          <Pill size="sm" variant="indigo-light">
            <Pill.Icon icon={faFile} />
            <Pill.Text>{fileCount}</Pill.Text>
          </Pill>
        </div>
      }
      description={category.description}
      label={category.display_name}
      value={category.category_key}
    >
      <div className="flex flex-col gap-3">
        {category.file_ids.map((fileId) => {
          const isNewlyUploaded = newlyUploadedFileIds?.includes(fileId);

          return (
            <CaseFileCard key={fileId} fileId={fileId}>
              {(onView) => (
                <FileCard
                  badge={
                    isNewlyUploaded ? (
                      <Pill
                        dataLoc={`file-new-pill-${fileId}`}
                        size="sm"
                        variant="green"
                      >
                        <Pill.Text>New</Pill.Text>
                      </Pill>
                    ) : undefined
                  }
                  dataLoc={`file-card-${fileId}`}
                  rightSlot={(fileInfo) => {
                    const elements = buildFileDropdownElements(
                      fileId,
                      fileInfo.name,
                    );

                    if (elements.length === 0) return null;

                    return (
                      <EllipsisOptionsDropdown
                        dataLoc={`file-options-${fileId}`}
                        elements={elements}
                        iconSize="xs"
                      />
                    );
                  }}
                  fileEnv={env}
                  fileId={fileId}
                  onView={onView}
                  showCreatedAt
                />
              )}
            </CaseFileCard>
          );
        })}
      </div>
    </Accordion.Item>
  );
};
